/**
 * How large a header draws the customer's logo (Brand Kit, "Size in the header").
 *
 * One height for every logo made long names look small and square marks big: the header drew all of them 28 px tall and at most
 * 128 px wide. A logo is sized by the space it fills instead. Each size gives every logo the same area, then holds the height between
 * a floor and a ceiling and the width under a limit, so a long name grows a little and a square mark stays compact. A white logo on a
 * dark page is drawn 6% smaller, because white on dark looks bigger to the eye; a logo on its white plate is not white on dark and
 * keeps its height.
 *
 * The API keeps its own copy of the sizes (apps/api/src/modules/tenant-branding/dto/brand-theme.dto.ts, BRAND_LOGO_SIZES) and the
 * Brand Theme page's vocabulary guard compares the two.
 */

export const LOGO_SIZES = ['small', 'medium', 'large'] as const
export type LogoSize = (typeof LOGO_SIZES)[number]
export const DEFAULT_LOGO_SIZE: LogoSize = 'medium'

export const isLogoSize = (value: unknown): value is LogoSize => (LOGO_SIZES as readonly unknown[]).includes(value)

/** The admin app's header (64 px tall) or the employee portal's (at least 64 px, its logo a little larger). */
export type LogoSurface = 'header' | 'portal'

interface Box {
  /** The space every logo of this size fills, in px². */
  area: number
  min: number
  max: number
  maxWidth: number
}

const BOXES: Readonly<Record<LogoSurface, Readonly<Record<LogoSize, Box>>>> = {
  header: {
    small: { area: 2400, min: 20, max: 28, maxWidth: 150 },
    medium: { area: 3400, min: 22, max: 32, maxWidth: 180 },
    large: { area: 4600, min: 24, max: 36, maxWidth: 200 },
  },
  // Medium keeps the portal's 36 px for a wordmark, which it drew before sizes existed.
  portal: {
    small: { area: 3200, min: 24, max: 32, maxWidth: 170 },
    medium: { area: 4500, min: 26, max: 36, maxWidth: 200 },
    large: { area: 6000, min: 28, max: 40, maxWidth: 220 },
  },
}

/** White on a dark page looks bigger: it is drawn this much smaller. */
export const DARK_BALANCE = 0.94

/** What a picture that has not loaded yet, or has no shape to read, is taken to be: a wordmark about 3.4 times wider than tall. */
export const TYPICAL_ASPECT = 3.4

/** The height and width limit for a logo of this shape (width over height). `whiteOnDark`: drawn with nothing behind it on dark. */
export function logoBox(size: LogoSize, aspect: number, surface: LogoSurface, whiteOnDark = false): { height: number; maxWidth: number } {
  const box = BOXES[surface][isLogoSize(size) ? size : DEFAULT_LOGO_SIZE]
  const shape = Number.isFinite(aspect) && aspect > 0 ? aspect : TYPICAL_ASPECT
  let height = Math.min(box.max, Math.max(box.min, Math.sqrt(box.area / shape)))
  if (height * shape > box.maxWidth) height = box.maxWidth / shape
  const drawn = Math.round(height)
  return { height: whiteOnDark ? Math.round(drawn * DARK_BALANCE) : drawn, maxWidth: box.maxWidth }
}
